import { ProjektDetail } from '../../../../Models/projekt.model';
import {
  Anlagenteil,
  FELD_KENNZEICHNUNG,
  MessstellenListeDetail,
  PRUEFERGEBNIS_OPTIONEN,
  Pruefergebnis,
  SIGNAL_STATUS_OPTIONEN,
  SignalStatus,
  messstelleAnzeigeName,
  messstelleFeldMitAliassen,
  normalizePruefergebnis,
  normalizeSignalStatus
} from '../../../../Models/messstelle.model';

/**
 * Gemeinsame Typen und Aufbereitung für die drei Export-Dialoge des
 * IBN-Protokolls.
 *
 * Der Hauptdialog wählt nur den Umfang; die Messstellen-Dialoge (einzeln,
 * Sammel-PDF, ZIP) teilen sich die Aufbereitung einer Messstelle zur
 * Anzeige – damit steht in allen dreien dasselbe.
 */

/** Womit die Dialoge geöffnet werden. */
export interface IbnProtokollDialogData {
  projekt: ProjektDetail;
}

/** Umfang des Exports. Entspricht den drei Endpunkten des Servers. */
export type IbnProtokollUmfangAuswahl = 'projekt' | 'inbetriebnahme' | 'messstelle';

/**
 * Welche der drei Messstellen-Varianten gewählt wurde.
 *
 * 'einzeln'      – ein vollständiges PDF für eine Messstelle
 * 'combinedPdf'  – mehrere Messstellen gemeinsam in EINEM PDF
 * 'zip'          – je Messstelle ein eigenes PDF, gebündelt in einer ZIP
 */
export type IbnProtokollMessstellenVariante = 'einzeln' | 'combinedPdf' | 'zip';

/**
 * Die beiden Varianten, die dieselbe Mehrfachauswahl verwenden. Der Modus
 * ändert nur Beschriftung und Art des Exports, nicht die Auswahllogik.
 */
export type IbnMessstellenExportModus = Extract<
  IbnProtokollMessstellenVariante,
  'combinedPdf' | 'zip'
>;

/** Ergebnis des Hauptdialogs bzw. des Einzel-Messstellen-Dialogs. */
export interface IbnProtokollAuswahl {
  umfang: IbnProtokollUmfangAuswahl;
  projektId: number;
  inbetriebnahmeId?: number;
  messstelleId?: number;

  /** Nur bei `umfang === 'messstelle'`: welche der drei Varianten gemeint ist. */
  variante?: IbnProtokollMessstellenVariante;

  /** Name des gewählten Gegenstands – für die Rückmeldung im Aufrufer. */
  bezeichnung: string;
}

/** Womit die Mehrfachauswahl geöffnet wird. */
export interface IbnProtokollMessstellenDialogData extends IbnProtokollDialogData {
  modus: IbnMessstellenExportModus;
}

/** Ergebnis der Mehrfachauswahl – die endgültige Exportliste. */
export interface IbnProtokollMessstellenAuswahl {
  modus: IbnMessstellenExportModus;
  projektId: number;
  inbetriebnahmeId: number;
  inbetriebnahmeName: string;

  /** Die gewählten Messstellen in der Reihenfolge der Exportliste. */
  messstellen: MessstelleAuswahlOption[];
}

/** Fortschritt einer Messstelle, wie ihn die Messstellenliste anzeigt. */
export type MessstellePruefStatus = 'Nicht gestartet' | 'Offen' | 'Geprüft';

/** Eine Messstelle, aufbereitet für die Auswahl im Export-Dialog. */
export interface MessstelleAuswahlOption {
  id: number;
  rowNumber: number;

  /** Bestehender Anzeigename – Fallback, wenn keine Kennzeichnung gepflegt ist. */
  name: string;

  /** KKS / AKZ / Messkreisnummer / … – je nach Kennzeichnungsart der Liste. */
  kennzeichnung: string;
  langtext: string;

  kategorieId: number | null;
  kategorieName: string;

  anlagenteilId: number | null;
  anlagenteilName: string;

  signalStatus: SignalStatus;
  pruefergebnis: Pruefergebnis;
  pruefStatus: MessstellePruefStatus;

  /** Vorberechnetes Suchfeld in Kleinschreibung – spart Arbeit je Tastendruck. */
  suchtext: string;
}

/**
 * Liest ein semantisches Standardfeld aus einer Messstelle.
 *
 * Wichtig:
 * Die Excel-Spalte kann z.B. "Bezeichnung", "Beschriftung" usw. heißen.
 * Maßgeblich ist das beim Import gespeicherte FieldMapping.
 */
export function standardFeldWert(
  values: { [key: string]: string } | null | undefined,
  fieldMapping: { [key: string]: string } | null | undefined,
  standardFeld: string
): string {

  const werte = values ?? {};
  const mapping = fieldMapping ?? {};

  for (const feld of messstelleFeldMitAliassen(standardFeld)) {

    // Über das KI-/Import-Mapping:
    // z.B. "Langtext" -> "Bezeichnung"
    const excelSpalte = mapping[feld];

    if (excelSpalte) {
      const wert = (werte[excelSpalte] ?? '').trim();

      if (wert) {
        return wert;
      }
    }

    // Fallback für Listen, deren Excel-Spalte bereits so heißt.
    const direkt = (werte[feld] ?? '').trim();

    if (direkt) {
      return direkt;
    }
  }

  return '';
}

/**
 * Platzhalter, die in der Excel „kein Wert“ bedeuten.
 *
 * Ein Strich ist keine Kategorie und kein Anlagenteil. Ohne diese
 * Vereinheitlichung stünden `null`, Leerstring, Leerzeichen und „–“ als vier
 * verschiedene Optionen im Filter, obwohl sie alle dasselbe meinen.
 */
const LEER_PLATZHALTER = ['-', '–', '—', '‒', '_'];

/** Trimmt einen Wert und macht aus einem Platzhalter einen Leerstring. */
export function bereinigterFeldWert(value: string | null | undefined): string {
  const wert = (value ?? '').trim();

  return LEER_PLATZHALTER.includes(wert) ? '' : wert;
}

/**
 * Derselbe Prüfstatus wie in der normalen Messstellenliste.
 */
export function messstellePruefStatus(
  gesamt: number | null | undefined,
  erledigt: number | null | undefined
): MessstellePruefStatus {

  const anzahlGesamt = gesamt ?? 0;
  const anzahlErledigt = erledigt ?? 0;

  if (anzahlGesamt === 0) {
    return 'Nicht gestartet';
  }

  if (anzahlErledigt >= anzahlGesamt) {
    return 'Geprüft';
  }

  return 'Offen';
}

/**
 * Bereitet die Messstellen einer Inbetriebnahme für die Auswahl auf.
 *
 * Einzige Quelle für alle drei Messstellen-Dialoge: die Kennzeichnung kommt
 * über das Feldmapping (also semantisch, nicht als fest verdrahtetes „KKS“),
 * der Prüfstatus aus denselben Zahlen wie in der Messstellenliste.
 */
export function messstellenAuswahlOptionen(
  liste: MessstellenListeDetail
): MessstelleAuswahlOption[] {

  return (liste.messstellen ?? []).map(m => {

    const kennzeichnung = standardFeldWert(
      m.values,
      liste.fieldMapping,
      FELD_KENNZEICHNUNG
    );

    const langtext = standardFeldWert(
      m.values,
      liste.fieldMapping,
      'Langtext'
    );

    const name = messstelleAnzeigeName(
      m.values,
      liste.fieldMapping,
      m.rowNumber
    );

    // Ohne Klartext gilt die Zuordnung als nicht vorhanden – ein „–“ in der
    // Spalte darf im Filter keine eigene Option werden.
    const kategorieName = bereinigterFeldWert(m.kategorieName);
    const anlagenteilName = bereinigterFeldWert(m.anlagenteilName);

    return {
      id: m.id,
      rowNumber: m.rowNumber,
      name,

      kennzeichnung,
      langtext,

      kategorieId: kategorieName ? (m.kategorieId ?? null) : null,
      kategorieName,

      anlagenteilId: anlagenteilName ? (m.anlagenteilId ?? null) : null,
      anlagenteilName,

      signalStatus: normalizeSignalStatus(m.signalStatus),
      pruefergebnis: normalizePruefergebnis(m.pruefergebnis),

      pruefStatus: messstellePruefStatus(
        m.pruefpunkteGesamt,
        m.pruefpunkteErledigt
      ),

      // Gesucht wird mindestens über Kennzeichnung und Langtext; Kategorie,
      // Anlagenteil und Zeilennummer kosten nichts und helfen beim Finden.
      suchtext: [
        kennzeichnung,
        langtext,
        name,
        kategorieName,
        anlagenteilName,
        String(m.rowNumber)
      ].join(' ').toLowerCase()
    };
  });
}

// ── Filteroptionen ──────────────────────────────────────────────────────────

/** Ein auswählbarer Anlagenteil im Filter. */
export interface AnlagenteilFilterOption {
  id: number;
  name: string;
}

/**
 * Die auswählbaren Werte aller Filter – abgeleitet aus den Messstellen EINER
 * Inbetriebnahme.
 *
 * Bewusst nicht aus den Katalogen des Projekts oder der Datenbank: dort
 * stünden auch Anlagenteile, Kategorien und Status, die in dieser
 * Inbetriebnahme gar nicht vorkommen. Ein Filter, der garantiert null Treffer
 * liefert, ist keine Hilfe, sondern eine Falle.
 */
export interface MessstellenFilterOptionen {
  /** Anlagenteile, die in dieser Inbetriebnahme tatsächlich vorkommen. */
  anlagenteile: AnlagenteilFilterOption[];

  /** Nur wahr, wenn mindestens eine Messstelle ohne Anlagenteil dabei ist. */
  hatOhneAnlagenteil: boolean;

  kategorien: string[];
  hatOhneKategorie: boolean;

  signalStatus: { wert: SignalStatus; text: string; css: string }[];
  pruefergebnis: { wert: Pruefergebnis; text: string; css: string }[];
}

/** Ausgangszustand, solange keine Inbetriebnahme geladen ist. */
export function leereFilterOptionen(): MessstellenFilterOptionen {
  return {
    anlagenteile: [],
    hatOhneAnlagenteil: false,
    kategorien: [],
    hatOhneKategorie: false,
    signalStatus: [],
    pruefergebnis: []
  };
}

/**
 * Leitet die Filteroptionen aus den geladenen Messstellen ab.
 *
 * Jeder Wert kommt genau einmal vor. Anlagenteile folgen der Reihenfolge der
 * Inbetriebnahme, sofern bekannt – die ist fachlich sortiert und nicht
 * alphabetisch; alles Übrige wird alphabetisch angeordnet. Status und
 * Prüfergebnis behalten die Reihenfolge ihres Katalogs, denn sie beschreibt
 * den typischen Ablauf.
 *
 * Die Anlagenteil-Reihenfolge dient ausschließlich der Sortierung: welche
 * Anlagenteile überhaupt erscheinen, entscheiden allein die Messstellen.
 */
export function messstellenFilterOptionen(
  messstellen: MessstelleAuswahlOption[],
  anlagenteilReihenfolge?: Anlagenteil[] | null
): MessstellenFilterOptionen {

  const anlagenteilNamen = new Map<number, string>();
  const kategorien = new Set<string>();
  const signalStatus = new Set<SignalStatus>();
  const pruefergebnis = new Set<Pruefergebnis>();

  let hatOhneAnlagenteil = false;
  let hatOhneKategorie = false;

  for (const messstelle of messstellen) {

    // null, Leerstring, Leerzeichen und „–“ sind in der Aufbereitung bereits
    // auf denselben Zustand gebracht worden – hier zählt nur noch ja/nein.
    if (messstelle.anlagenteilId == null) {
      hatOhneAnlagenteil = true;
    } else if (!anlagenteilNamen.has(messstelle.anlagenteilId)) {
      anlagenteilNamen.set(messstelle.anlagenteilId, messstelle.anlagenteilName);
    }

    if (messstelle.kategorieName) {
      kategorien.add(messstelle.kategorieName);
    } else {
      hatOhneKategorie = true;
    }

    signalStatus.add(messstelle.signalStatus);
    pruefergebnis.add(messstelle.pruefergebnis);
  }

  const sortierung = new Map<number, number>(
    (anlagenteilReihenfolge ?? []).map(a => [a.id, a.sortOrder])
  );

  return {
    anlagenteile: Array.from(anlagenteilNamen, ([id, name]) => ({ id, name }))
      .sort((a, b) => {
        const sortA = sortierung.get(a.id);
        const sortB = sortierung.get(b.id);

        if (sortA != null && sortB != null && sortA !== sortB) {
          return sortA - sortB;
        }

        return a.name.localeCompare(b.name, 'de');
      }),

    hatOhneAnlagenteil,

    kategorien: Array.from(kategorien).sort((a, b) => a.localeCompare(b, 'de')),
    hatOhneKategorie,

    signalStatus: SIGNAL_STATUS_OPTIONEN.filter(o => signalStatus.has(o.wert)),
    pruefergebnis: PRUEFERGEBNIS_OPTIONEN.filter(o => pruefergebnis.has(o.wert))
  };
}

/** Anzeigename einer Messstelle in Listen und Dateinamen. */
export function auswahlAnzeigeName(option: MessstelleAuswahlOption): string {
  return option.kennzeichnung || option.name;
}
